// Pass-rate over runs — trend over time, ONE series.
// Single series ⇒ no legend box (the card title names it); the endpoint is
// direct-labelled; deploy markers are annotations, drawn as solid hairlines
// (dashed rules are an anti-pattern — they read as "threshold").
import { useState } from 'react';
import { useChart, tooltipStyle, axisStyle } from '../lib/useChart';
import { chrome, seriesOne } from '../lib/palette';
import { pct, shortDate, shortTime, runName, esc } from '../lib/format';
import ChartCard from './ChartCard';

/**
 * X-axis tick: the RUN is the unit, and nobody parses 11-digit run ids — date on
 * top, start time underneath. Runs are not on a cadence (weekly once, sometimes
 * 2–3 in a day); the time line keeps same-day runs distinguishable.
 */
function runLabel(t) {
  return `${shortDate(t.started_at)}\n${shortTime(t.started_at)}`;
}

export default function PassRateChart({ trend, mode, selectedRun = null, onPickRun = null }) {
  const [showTable, setShowTable] = useState(false);
  const c = chrome(mode);
  const color = seriesOne(mode);

  const [ref] = useChart(
    () => ({
      backgroundColor: 'transparent',
      // Top band leaves room for the deploy carets; bottom band fits the two-line
      // run+date labels so the card never grows a nested scrollbar.
      grid: { left: 44, right: 56, top: 34, bottom: 46 },
      tooltip: {
        trigger: 'axis',
        // The crosshair finds the X — readers aim at a run, not at a 2px line.
        axisPointer: { type: 'line', lineStyle: { color: c.axis, width: 1 } },
        ...tooltipStyle(c),
        formatter: (params) => {
          const p = params[0];
          const run = trend[p.dataIndex];
          const deploy = run.is_deploy
            ? `<div style="color:${c.textSecondary};margin-top:4px">deploy ${esc(run.deploy_version)}</div>`
            : '';
          return (
            `<div style="font-weight:600;font-size:14px">${esc(pct(run.pass_rate))}</div>` +
            `<div style="color:${c.textSecondary}">${esc(runName(run))} · run ${esc(run.run_id)}</div>` +
            `<div style="color:${c.textSecondary}">${esc(run.failed)} failed of ${esc(run.executed)} executed</div>` +
            deploy
          );
        },
      },
      xAxis: {
        // One equally-spaced slot PER RUN — this axis is ordinal, not a time scale.
        // Equal spacing between a weekly gap and a same-day pair is deliberate: the
        // reader compares runs; the date line under each run id carries the "when".
        type: 'category',
        data: trend.map(runLabel),
        boundaryGap: false,
        ...axisStyle(c),
        axisLabel: { color: c.muted, fontSize: 10, lineHeight: 14 },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        scale: true,
        // A pass rate cannot exceed 100 — without this cap, a single-point series
        // made ECharts pad the axis to 140% (caught by loading a real one-run job).
        max: (v) => Math.min(100, Math.ceil(v.max + 1)),
        ...axisStyle(c),
        axisLine: { show: false },
        axisLabel: { color: c.muted, fontSize: 11, formatter: (v) => `${v}%` },
      },
      series: [
        {
          type: 'line',
          // Selected run's point renders larger — the filter state is visible on the chart.
          data: trend.map((t) => (t.run_id === selectedRun
            ? { value: t.pass_rate, symbolSize: 14, itemStyle: { borderWidth: 3 } }
            : t.pass_rate)),
          smooth: false,
          lineStyle: { width: 2, color, cap: 'round', join: 'round' },
          itemStyle: { color, borderColor: c.surface, borderWidth: 2 },
          symbol: 'circle',
          symbolSize: 8,
          // The hit area is bigger than the 8px mark — nobody reliably lands on a dot.
          // The axis crosshair already finds the X; this makes the point itself respond.
          emphasis: { scale: 1.6, itemStyle: { borderWidth: 3 } },
          // No area fill: this axis is truncated (pass rates live in a narrow band),
          // and a filled area under a non-zero baseline implies a magnitude from zero
          // that isn't there. The line alone carries the trend honestly.
          // Label the endpoint only — a number on every point is chaos.
          label: {
            show: true,
            position: 'right',
            color: c.textPrimary,
            fontSize: 12,
            fontWeight: 600,
            formatter: (p) => (p.dataIndex === trend.length - 1 ? pct(p.value) : ''),
          },
          // Deploy annotations: solid hairline (dashed rules read as "threshold"),
          // with a caret at the axis so the mark is findable rather than implied.
          markLine: {
            // Caret + label ride the TOP of the plot; at the bottom they collide with
            // the date ticks and overprint them into mush.
            symbol: ['none', 'triangle'],
            symbolSize: 7,
            symbolRotate: 180,
            silent: true,
            label: {
              show: true,
              position: 'end',
              formatter: 'deploy',
              color: c.muted,
              fontSize: 10,
              padding: [0, 0, 4, 0],
            },
            lineStyle: { color: c.axis, width: 1, type: 'solid' },
            data: trend.map((t, i) => (t.is_deploy ? { xAxis: i } : null)).filter(Boolean),
          },
        },
      ],
    }),
    [trend, selectedRun],
    {
      mode,
      // Click a run's point (or its x-slot) to filter the leaderboard to that run.
      onInit: (chart) => {
        if (!onPickRun) return;
        chart.getZr().on('click', (e) => {
          const [x] = chart.convertFromPixel({ seriesIndex: 0 }, [e.offsetX, e.offsetY]) || [];
          if (x === undefined || x === null) return;
          const i = Math.round(x);
          if (i >= 0 && i < trend.length) onPickRun(trend[i].run_id);
        });
      },
    }
  );

  return (
    <ChartCard
      title="Pass rate by run"
      subtitle="All runs · the enlarged point is the selected run — click a point or use the Run dropdown"
      showTable={showTable}
      onToggleTable={() => setShowTable((v) => !v)}
      table={
        <table className="data-table">
          <thead>
            <tr><th>Run</th><th className="num">Pass rate</th><th className="num">Failed</th><th>Deploy</th><th>Links</th></tr>
          </thead>
          <tbody>
            {trend.map((t) => (
              <tr key={t.run_id}>
                <td>{runName(t)}</td>
                <td className="num">{pct(t.pass_rate)}</td>
                <td className="num">{t.failed}</td>
                {/* MCIP-24: the version is shown whenever KNOWN; the deploy
                    caret (chart marker) still keys on is_deploy only. */}
                <td>{t.deploy_version || '—'}</td>
                <td>
                  {t.links?.allure_report && (
                    <a className="run-link" href={t.links.allure_report} target="_blank" rel="noreferrer">Allure ↗</a>
                  )}
                  {t.links?.github_run && (
                    <a className="run-link" href={t.links.github_run} target="_blank" rel="noreferrer">GitHub ↗</a>
                  )}
                  {!t.links?.allure_report && !t.links?.github_run && '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      }
    >
      <div ref={ref} className="chart chart--line" />
    </ChartCard>
  );
}
